import { translations } from "@eworkit/messenger";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import moment from "moment";
import { initReactI18next } from "react-i18next";

export type TDefaultLanguageKey = "en";
export type TOptionalLanguageKey = "da" | "sv" | "fi" | "no" | "pl";
export type TSupportedLanguageKey = TDefaultLanguageKey | TOptionalLanguageKey;

export interface ILangOption {
  code: TSupportedLanguageKey;
  value: string;
}

type TLangMap = { [key in TSupportedLanguageKey]: ILangOption };

export const languagesList: TLangMap = {
  en: {
    code: "en",
    value: "English",
  },
  sv: {
    code: "sv",
    value: "Svenska",
  },
  pl: {
    code: "pl",
    value: "Polski",
  },
  da: {
    code: "da",
    value: "Dansk",
  },
  fi: {
    code: "fi",
    value: "Suomi",
  },
  no: {
    code: "no",
    value: "Norsk",
  },
};

export const SUPPORTED_LANGUAGES = Object.values(languagesList).map(({ code }) => code);
export const DEFAULT_LANGUAGE = SUPPORTED_LANGUAGES[0];

const mapToMomentLocale = (lang: TSupportedLanguageKey) => {
  if (lang === "no") return "nb";
  return lang;
};

export const changeLanguage = (lang: TSupportedLanguageKey) => {
  moment.locale(mapToMomentLocale(lang));
  i18n.changeLanguage(lang);
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(
    {
      backend: {
        loadPath: "{lng}/{ns}",
        request: async function (_options, url, _payload, callback) {
          await import(/* webpackChunkName: "locales/[request]" */ `./locales/${url}.translations.json`)
            .then(module => callback(null, { data: module.default, status: 200 }))
            .catch(error => {
              // eslint-disable-next-line no-console
              console.error(`Cannot load translation file "./locales/${url}.translations.json"`, error);
              callback(error, { data: "", status: 404 });
            });
        },
      },
      supportedLngs: SUPPORTED_LANGUAGES,
      fallbackLng: "en",
      ns: "common",
      defaultNS: "common",
      react: { useSuspense: true },
      debug: false,
      keySeparator: ".",
      interpolation: {
        prefix: "{",
        suffix: "}",
        escapeValue: false,
      },
    },
    () => {
      moment.locale(mapToMomentLocale(i18n.language as TSupportedLanguageKey));
    },
  );

// TODO: Change this to lazy loaded translations instead of static loading
Object.keys(translations.locales).forEach((language: keyof typeof translations.locales) => {
  Object.keys(translations.locales[language]).forEach(
    (namespace: keyof typeof translations.locales[keyof typeof translations.locales]) => {
      i18n.addResourceBundle(language, namespace, translations.locales[language][namespace]);
    },
  );
});

export default i18n;
